import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TSupplierPaymentDetails } from './SupplierPayment';
import useFetchInvoice from './useFetchInvoice';
import { useBranchContext } from 'providers/BranchProvider';
import useFetchProcurementReturns from './useFetchProcurementReturns';
import {
  TProcurementInvoiceDetails,
  TProcurementReturns,
  TSupplierPayment
} from 'utils/types';
import { ColumnDef } from '@tanstack/react-table';
import { formatDate, formatNumberWithCommas } from 'utils/formats';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import axiosInstance from 'utils/axiosInterceptors';
import {
  Alert,
  Badge,
  Button,
  Card,
  Dropdown,
  DropdownButton,
  Form,
  Row
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import CreatePaymentProofForm, {
  UploadPaymentProofResult
} from './CreatePaymentProofForm';
import useFetchUserDetails from './useFetchUserDetails';
import CustomModal from 'components/modals/CustomModal';
import ReleaseCheckForm from './ReleaseCheckForm';
import ConfirmCheckForm from './ConfirmCheckForm';
import BadgeStatus from 'utils/BadgeStatus';
import ImageModal from 'components/modals/ImageModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEdit } from '@fortawesome/free-solid-svg-icons';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import EditSupplierPaymentForm from './EditSupplierPaymentForm';
import {
  DocumentPreview,
  parseDocumentPreviewResponse,
  revokeDocumentPreview
} from 'utils/printHtmlAsPdf';
import HtmlPdfPreviewModal from 'components/modals/HtmlPdfPreviewModal';
import { useAuth } from 'hooks/useAuth';
import { FeatureRoles, hasAnyRole } from 'utils/roleAccess';

type TSelectedInvoiceToReturn = {
  supplierName?: string | undefined;
  procurementInvoiceId?: number | undefined;
  dueDate?: string | undefined;
  total?: number | undefined;
};

type TSelectedProcurementReturn = {
  procurementReturnId?: number | undefined;
  supplierName?: string | undefined;
  productName?: string | undefined;
  quantity?: number | null;
  amount?: number | undefined;
  status?: string | undefined;
};

type TSupplierPaymentReturns = {
  procurementReturnId: number;
  productId: number;
  productName: string;
  productBrandId: number;
  productBrandName: string;
  status: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  createdOn: string;
};

const SupplierPaymentDetails = () => {
  const { id: supplierPaymentId } = useParams();
  const navigate = useNavigate();
  const { selectedBranch } = useBranchContext();
  const { token } = useAuth();
  const isAdmin = hasAnyRole(token?.roles, FeatureRoles.adminOnly);
  const [supplierPayment, setSupplierPayment] =
    useState<TSupplierPayment | null>(null);
  const [supplierPaymentDetails, setSupplierPaymentDetails] =
    useState<TSupplierPaymentDetails>({
      supplierPaymentMethod: '',
      accountDate: '',
      supplierId: 0,
      procurementInvoiceIds: [],
      procurementReturnIds: [],
      paymentNotes: ''
    });
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<DocumentPreview | null>(null);

  // invoices && procurementReturns are the sets of items to be selected to create a payment
  const { invoices, fetchInvoice } = useFetchInvoice({
    id: supplierPayment?.supplierId,
    branchDomain: selectedBranch?.branchDomain as string,
    paymentStatus: 'Unpaid'
  });
  const { procurementReturns } = useFetchProcurementReturns({
    id: supplierPayment?.supplierId,
    branchDomain: selectedBranch?.branchDomain as string
  });

  const { userDetails } = useFetchUserDetails();
  const [selectedInvoice, setSelectedInvoice] = useState<
    TSelectedInvoiceToReturn[]
  >([]);
  const [selectedReturns, setSelectedReturns] = useState<
    TSelectedProcurementReturn[]
  >([]);
  const [invoice, setInvoice] = useState<TProcurementInvoiceDetails[]>([]);
  const [selectedPaymentInvoiceIds, setSelectedPaymentInvoiceIds] = useState<
    number[]
  >([]);
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);
  const [isDeletingInvoices, setIsDeletingInvoices] = useState(false);
  const [returns, setReturns] = useState<TSupplierPaymentReturns[]>([]);
  const [initiateSelection, setInitiateSelection] = useState(false);
  const [showReturns, setShowReturns] = useState(false);
  const [paymentProofImage, setPaymentProofImage] = useState<string>('');
  const [checkNumberDraft, setCheckNumberDraft] = useState('');
  const [hasPaymentProof, setHasPaymentProof] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingReturns, setLoadingReturns] = useState(false);

  const modalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => {},
    hide: () => {}
  });
  const releaseCheckModalRef = React.useRef<{
    show: () => void;
    hide: () => void;
  }>({
    show: () => {},
    hide: () => {}
  });
  const confirmCheckModalRef = React.useRef<{
    show: () => void;
    hide: () => void;
  }>({
    show: () => {},
    hide: () => {}
  });
  const editSupplierPaymentModalRef = React.useRef<{
    show: () => void;
    hide: () => void;
  }>({
    show: () => {},
    hide: () => {}
  });
  const deletePaymentModalRef = React.useRef<{
    show: () => void;
    hide: () => void;
  }>({
    show: () => {},
    hide: () => {}
  });
  const deleteInvoicesModalRef = React.useRef<{
    show: () => void;
    hide: () => void;
  }>({
    show: () => {},
    hide: () => {}
  });
  const formRef = React.useRef<{
    submitForm: () => Promise<
      true | UploadPaymentProofResult | false | undefined
    >;
  } | null>(null);
  const releaseCheckFormRef = React.useRef<{
    submitForm: () => Promise<boolean | undefined>;
  }>(null);
  const confirmCheckFormRef = React.useRef<{
    submitForm: () => Promise<boolean | undefined>;
  }>(null);
  const editSupplierPaymentFormRef = React.useRef<{
    submitForm: () => Promise<boolean | undefined>;
  }>(null);

  const imageModalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => {},
    hide: () => {}
  });

  const fetchSupplierPayment = useCallback(async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/supplierPayments/${supplierPaymentId}`
      );

      const { data } = res;
      setSupplierPayment(data);
      setCheckNumberDraft(data.checkNumber || '');
      handleFormChange('supplierPaymentMethod', data.supplierPaymentMethod);
      handleFormChange('accountDate', data.accountDate);
      handleFormChange('supplierId', data.supplierId);
      handleFormChange('paymentNotes', data.paymentNotes || '');
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    }
  }, [selectedBranch?.branchDomain, supplierPaymentId]);

  const fetchSupplierInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/supplierPayments/${supplierPaymentId}/procurementInvoices?page=1&pageSize=100`
      );
      const { data } = res;
      setInvoice(data.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    } finally {
      setLoadingInvoices(false);
    }
  }, [selectedBranch?.branchDomain, supplierPaymentId]);

  const fetchSupplierReturns = useCallback(async () => {
    setLoadingReturns(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/supplierPayments/${supplierPaymentId}/procurementReturns?page=1&pageSize=100`
      );
      const { data } = res;
      setReturns(data.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    } finally {
      setLoadingReturns(false);
    }
  }, [selectedBranch?.branchDomain, supplierPaymentId]);

  const fetchPaymentProofImage = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/supplierPayments/${supplierPaymentId}/paymentProofImage`,
        {
          responseType: 'blob'
        }
      );
      const imageUrl = URL.createObjectURL(res.data);
      setHasPaymentProof(true);
      setPaymentProofImage(imageUrl);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFormChange = <K extends keyof TSupplierPaymentDetails>(
    field: K,
    value: TSupplierPaymentDetails[K]
  ) => {
    setSupplierPaymentDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInvoiceCheckboxChange = (item: TProcurementInvoiceDetails) => {
    const isSelected = selectedInvoice.some(
      selected => item.procurementInvoiceId === selected.procurementInvoiceId
    );

    let updatedSelectedInvoice;

    if (isSelected) {
      updatedSelectedInvoice = selectedInvoice.filter(
        selected => selected.procurementInvoiceId !== item.procurementInvoiceId
      );
    } else {
      updatedSelectedInvoice = [
        ...selectedInvoice,
        {
          supplierName: item.supplierName,
          procurementInvoiceId: item.procurementInvoiceId,
          dueDate: item.dueDate,
          total: item.total
        }
      ];
    }

    setSelectedInvoice(updatedSelectedInvoice);

    const selectedInvoiceIdList = updatedSelectedInvoice.map(
      selected => selected.procurementInvoiceId
    );

    setSupplierPaymentDetails(prev => ({
      ...prev,
      procurementInvoiceIds: selectedInvoiceIdList
    }));
  };

  const handleReturnsCheckboxChange = (item: TProcurementReturns) => {
    const isSelected = selectedReturns.some(
      selected => item.procurementReturnId === selected.procurementReturnId
    );

    let updatedSelectedReturns;

    if (isSelected) {
      updatedSelectedReturns = selectedReturns.filter(
        selected => selected.procurementReturnId !== item.procurementReturnId
      );
    } else {
      updatedSelectedReturns = [
        ...selectedReturns,
        {
          procurementReturnId: item.procurementReturnId,
          supplierName: item.supplierName,
          productName: item.productName,
          quantity: item.quantity,
          amount: item.amount,
          status: item.status
        }
      ];
    }

    setSelectedReturns(updatedSelectedReturns);

    const selectedProcurementIdList = updatedSelectedReturns.map(
      selected => selected.procurementReturnId
    );

    setSupplierPaymentDetails(prev => ({
      ...prev,
      procurementReturnIds: selectedProcurementIdList
    }));
  };

  const togglePaymentInvoiceSelection = useCallback((invoiceId: number) => {
    setSelectedPaymentInvoiceIds(prev =>
      prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  }, []);

  const toggleAllPaymentInvoices = useCallback(() => {
    const invoiceIds = invoice
      .map(item => item.procurementInvoiceId)
      .filter((id): id is number => typeof id === 'number');
    setSelectedPaymentInvoiceIds(prev =>
      invoiceIds.length > 0 && prev.length === invoiceIds.length ? [] : invoiceIds
    );
  }, [invoice]);

  const handleDeletePayment = async () => {
    if (!selectedBranch?.branchDomain || !supplierPaymentId) return;

    setIsDeletingPayment(true);
    try {
      await axiosInstance.delete(
        `/api/v1/${selectedBranch.branchDomain}/supplierPayments/${supplierPaymentId}`
      );
      toast.success('Supplier payment deleted.', {
        ...toastConfig,
        autoClose: 2000
      });
      deletePaymentModalRef.current.hide();
      navigate('/supplier-payments');
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            'Failed to delete supplier payment.',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    } finally {
      setIsDeletingPayment(false);
    }
  };

  const handleDeleteSelectedInvoices = async () => {
    if (!selectedBranch?.branchDomain || !supplierPaymentId) return;
    if (selectedPaymentInvoiceIds.length === 0) return;

    setIsDeletingInvoices(true);
    try {
      await axiosInstance.post(
        `/api/v1/${selectedBranch.branchDomain}/supplierPayments/${supplierPaymentId}/invoices/delete`,
        { procurementInvoiceIds: selectedPaymentInvoiceIds }
      );
      toast.success('Selected invoices removed from this payment.', {
        ...toastConfig,
        autoClose: 2000
      });
      deleteInvoicesModalRef.current.hide();
      setSelectedPaymentInvoiceIds([]);
      await Promise.all([
        fetchSupplierPayment(),
        fetchSupplierInvoices(),
        fetchInvoice()
      ]);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            'Failed to delete selected invoices.',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    } finally {
      setIsDeletingInvoices(false);
    }
  };

  const invoiceColumns: ColumnDef<TProcurementInvoiceDetails>[] = useMemo(
    () => [
      {
        id: 'Select',
        header: 'Select',
        cell: ({ row }: { row: { original: TProcurementInvoiceDetails } }) => {
          const item = row.original;
          const isSelected = selectedInvoice.some(
            selected =>
              item.procurementInvoiceId === selected.procurementInvoiceId
          );
          return (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleInvoiceCheckboxChange(item)}
            />
          );
        }
      },
      {
        id: 'supplierName',
        header: 'Supplier Name',
        accessorKey: 'supplierName',
        enableSorting: false
      },
      {
        id: 'invoiceNumber',
        header: 'invoice Number',
        accessorKey: 'invoiceNumber',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return (
            <Link
              to={`/procurement-invoices/${original.procurementInvoiceId}`}
              className="text-decoration-none fw-bold fs-9"
            >
              {original.invoiceNumber}
            </Link>
          );
        }
      },
      {
        id: 'dueDate',
        header: 'Due Date',
        accessorKey: 'dueDate',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          const formattedDate =
            original.dueDate !== null ? formatDate(original.dueDate ?? '') : '';
          return <span>{formattedDate}</span>;
        }
      },
      {
        id: 'remarks',
        header: 'Remarks',
        accessorKey: 'remarks',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return (
            <span style={{ whiteSpace: 'pre-wrap' }}>
              {original.remarks || '—'}
            </span>
          );
        }
      },
      {
        id: 'total',
        header: 'Total',
        accessorKey: 'total',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return (
            <div>
              <span>{formatNumberWithCommas(original.totalPayable)}</span>
              {original.hasReturns == true && (
                <Badge bg="warning" className="fs-10 ms-2">
                  Returns
                </Badge>
              )}
            </div>
          );
        }
      }
    ],
    [selectedInvoice, handleInvoiceCheckboxChange]
  );

  const procurementReturnsColumn: ColumnDef<TProcurementReturns>[] = useMemo(
    () => [
      {
        id: 'Select',
        header: 'Select',
        cell: ({ row }: { row: { original: TProcurementReturns } }) => {
          const item = row.original;
          const isSelected = selectedReturns.some(
            selected =>
              item.procurementReturnId === selected.procurementReturnId
          );
          return (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleReturnsCheckboxChange(item)}
            />
          );
        }
      },
      {
        id: 'supplierName',
        header: 'Supplier Name',
        accessorKey: 'supplierName',
        enableSorting: false
      },
      {
        id: 'productName',
        header: 'Product Name',
        accessorKey: 'productName',
        enableSorting: false
      },
      {
        id: 'quantity',
        header: 'Quantity',
        accessorKey: 'quantity',
        enableSorting: false
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorKey: 'amount',
        enableSorting: false
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        enableSorting: false
      }
    ],
    [selectedReturns, handleReturnsCheckboxChange]
  );

  const paymentInvoiceColumn: ColumnDef<TProcurementInvoiceDetails>[] = useMemo(
    () => [
      ...(isAdmin
        ? [
            {
              id: 'Select',
              header: () => {
                const invoiceIds = invoice
                  .map(item => item.procurementInvoiceId)
                  .filter((id): id is number => typeof id === 'number');
                const allSelected =
                  invoiceIds.length > 0 &&
                  selectedPaymentInvoiceIds.length === invoiceIds.length;
                return (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAllPaymentInvoices}
                    aria-label="Select all payment invoices"
                  />
                );
              },
              cell: ({
                row
              }: {
                row: { original: TProcurementInvoiceDetails };
              }) => {
                const invoiceId = row.original.procurementInvoiceId;
                if (invoiceId == null) return null;
                return (
                  <input
                    type="checkbox"
                    checked={selectedPaymentInvoiceIds.includes(invoiceId)}
                    onChange={() => togglePaymentInvoiceSelection(invoiceId)}
                  />
                );
              }
            } as ColumnDef<TProcurementInvoiceDetails>
          ]
        : []),
      {
        id: 'invoiceNumber',
        header: 'Invoice Number',
        accessorKey: 'invoiceNumber',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return (
            <Link
              to={`/procurement-invoices/${original.procurementInvoiceId}`}
              className="text-decoration-none fw-bold fs-9"
            >
              {original.invoiceNumber}
            </Link>
          );
        }
      },
      {
        id: 'remarks',
        header: 'Remarks',
        accessorKey: 'remarks',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return (
            <span style={{ whiteSpace: 'pre-wrap' }}>
              {original.remarks || '—'}
            </span>
          );
        }
      },
      {
        id: 'total',
        header: 'Total',
        accessorKey: 'total',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return formatNumberWithCommas(original.total);
        }
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        enableSorting: false
      },
      {
        id: 'dueDate',
        header: 'Due Date',
        accessorKey: 'dueDate',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return formatDate(original.dueDate as string);
        }
      }
    ],
    [
      invoice,
      isAdmin,
      selectedPaymentInvoiceIds,
      toggleAllPaymentInvoices,
      togglePaymentInvoiceSelection
    ]
  );

  const paymentReturnColumn: ColumnDef<TSupplierPaymentReturns>[] = useMemo(
    () => [
      {
        id: 'productName',
        header: 'Product Name',
        accessorKey: 'productName',
        enableSorting: false
      },
      {
        id: 'quantity',
        header: 'Quantity',
        accessorKey: 'quantity',
        enableSorting: false
      },
      {
        id: 'unitPrice',
        header: 'Unit Price',
        accessorKey: 'unitPrice',
        enableSorting: false
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorKey: 'amount',
        enableSorting: false
      }
    ],
    [selectedReturns, handleReturnsCheckboxChange]
  );

  const invoiceTable = useAdvanceTable({
    data: invoices,
    columns: invoiceColumns,
    pageSize: 5,
    pagination: true,
    sortable: true
  });

  const procurementReturnsTable = useAdvanceTable({
    data: procurementReturns,
    columns: procurementReturnsColumn,
    pageSize: 5,
    pagination: true,
    sortable: true
  });

  const paymentInvoiceTable = useAdvanceTable({
    data: invoice,
    columns: paymentInvoiceColumn,
    pageSize: 20,
    pagination: true,
    sortable: true
  });

  const paymentReturnTable = useAdvanceTable({
    data: returns,
    columns: paymentReturnColumn,
    pageSize: 20,
    pagination: true,
    sortable: true
  });

  const handleSubmitSelection = async () => {
    const selectedInvoiceIdList = selectedInvoice.map(
      invoice => invoice.procurementInvoiceId
    );
    const selectedProcurementIdList = selectedReturns.map(
      invoice => invoice.procurementReturnId
    );
    setSupplierPaymentDetails(prevDetails => ({
      ...prevDetails,
      procurementInvoiceIds: selectedInvoiceIdList,
      procurementReturnIds: selectedProcurementIdList
    }));
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/supplierPayments/${supplierPaymentId}`,
        supplierPaymentDetails
      );
      toast.success('Selected Item/s Submitted', {
        ...toastConfig,
        autoClose: 2000
      });
      await Promise.all([
        fetchSupplierPayment(),
        fetchSupplierInvoices(),
        fetchSupplierReturns()
      ]);
      setInitiateSelection(false);
      setSelectedInvoice([]);
      setSelectedReturns([]);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleSubmitProofOfPayment = async () => {
    if (formRef.current) {
      try {
        const result = await formRef.current.submitForm();
        if (result === true) {
          await Promise.all([fetchSupplierPayment(), fetchPaymentProofImage()]);
          modalRef.current.hide();
        } else if (
          result &&
          typeof result === 'object' &&
          (result as UploadPaymentProofResult).isCheckNumberPossibleMismatch
        ) {
          await Promise.all([fetchSupplierPayment(), fetchPaymentProofImage()]);
          modalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleUpdateCheckNumber = async (checkNumber: string) => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/supplierPayments/${supplierPaymentId}/checkNumber`,
        { checkNumber, confirmEnteredNumber: false }
      );
      toast.success('Check number updated.', {
        ...toastConfig,
        autoClose: 3000
      });
      await fetchSupplierPayment();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage || 'Failed to update check number.',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    }
  };

  const handleConfirmCheckNumber = async () => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/supplierPayments/${supplierPaymentId}/checkNumber`,
        { confirmEnteredNumber: true }
      );
      toast.success('Check number confirmed.', {
        ...toastConfig,
        autoClose: 3000
      });
      await fetchSupplierPayment();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            'Failed to confirm check number.',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    }
  };

  const handleReleaseCheck = async () => {
    if (releaseCheckFormRef.current) {
      try {
        const isSuccess = await releaseCheckFormRef.current.submitForm();
        if (isSuccess) {
          fetchSupplierPayment();
          releaseCheckModalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleConfirmCheck = async () => {
    if (confirmCheckFormRef.current) {
      try {
        const isSuccess = await confirmCheckFormRef.current.submitForm();
        if (isSuccess) {
          fetchSupplierPayment();
          confirmCheckModalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleSubmitEditedForm = async () => {
    if (editSupplierPaymentFormRef.current) {
      try {
        const isSuccess = await editSupplierPaymentFormRef.current.submitForm();
        if (isSuccess) {
          fetchSupplierPayment();
          editSupplierPaymentModalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    setPdfPreview(prev => {
      revokeDocumentPreview(prev);
      return null;
    });
  };

  const handleGeneratePdf = async () => {
    setPdfPreview(prev => {
      revokeDocumentPreview(prev);
      return null;
    });
    setShowPdfPreview(true);
    setIsGeneratingPdf(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/supplierPayments/${supplierPaymentId}/summary`,
        {
          responseType: 'blob'
        }
      );
      const parsed = await parseDocumentPreviewResponse(
        res,
        'Supplier Payment Summary.pdf'
      );
      setPdfPreview(parsed);
    } catch (error) {
      console.error(error);
      closePdfPreview();
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to generate supplier payment summary PDF.',
        {
          ...toastConfig,
          autoClose: 3000
        }
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    fetchSupplierPayment();
    fetchSupplierInvoices();
    fetchSupplierReturns();
  }, [fetchSupplierPayment, fetchSupplierInvoices, selectedBranch]);

  useEffect(() => {
    if (!loadingInvoices && !loadingReturns) {
      if (invoice.length === 0 && returns.length === 0) {
        setInitiateSelection(true);
      } else {
        setInitiateSelection(false);
      }
    }
  }, [loadingInvoices, loadingReturns, invoice, returns]);

  console.log(invoice.length == 0 && returns.length == 0 ? 't' : 'f');

  useEffect(() => {
    if (invoice.length !== 0 || returns.length !== 0) {
      setShowReturns(true);
    } else {
      setShowReturns(false);
    }
  }, [invoice, returns]);

  useEffect(() => {
    if (supplierPayment?.status && supplierPayment.status !== 'New') {
      fetchPaymentProofImage();
    }
  }, [
    supplierPayment?.status,
    selectedBranch?.branchDomain,
    supplierPaymentId
  ]);

  useEffect(() => {
    return () => {
      if (paymentProofImage) {
        URL.revokeObjectURL(paymentProofImage);
      }
    };
  }, [paymentProofImage]);

  return (
    <>
      <HtmlPdfPreviewModal
        show={showPdfPreview}
        onHide={closePdfPreview}
        title="Supplier Payment Summary PDF"
        preview={pdfPreview}
        isLoading={isGeneratingPdf}
      />
      <Row lg="auto" className="d-flex justify-content-between mb-3">
        <h4>Supplier Payments</h4>
        <div>
          {(supplierPayment?.status !== 'Paid' || isAdmin) && (
            <DropdownButton title="Actions" variant="phoenix-primary">
              {supplierPayment?.status == 'New' && hasPaymentProof == false && (
                <Dropdown.Item onClick={() => setInitiateSelection(true)}>
                  Add Item
                </Dropdown.Item>
              )}
              {supplierPayment?.status !== 'Paid' &&
                supplierPayment?.status !== 'CheckReleased' &&
                supplierPayment?.status !== 'New' && (
                  <Dropdown.Item
                    onClick={() => releaseCheckModalRef.current.show()}
                  >
                    Release Check
                  </Dropdown.Item>
                )}
              {supplierPayment?.status === 'CheckReleased' && (
                <Dropdown.Item
                  onClick={() => confirmCheckModalRef.current.show()}
                >
                  Confirm Check
                </Dropdown.Item>
              )}
              {isAdmin && (
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => deletePaymentModalRef.current.show()}
                >
                  Delete payment
                </Dropdown.Item>
              )}
            </DropdownButton>
          )}
        </div>
      </Row>
      {supplierPayment ? (
        <Card className="mb-5">
          <Card.Body className="d-flex">
            <div
              style={paymentProofImage ? { width: '40%' } : { width: '75%' }}
            >
              <Card.Title as="h6">
                Supplier Name: {supplierPayment?.supplierName}
              </Card.Title>
              <Card.Title as="h6">
                Account Date:{' '}
                {formatDate(supplierPayment?.accountDate as string)}
              </Card.Title>
              <Card.Title as="h6">
                Total: {formatNumberWithCommas(supplierPayment?.amount)}
              </Card.Title>
              <Card.Title as="h6" className="mb-1">
                Remarks
              </Card.Title>
              <p className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                {supplierPayment.paymentNotes || '—'}
              </p>
              <Button onClick={handleGeneratePdf}>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Generate PDF
              </Button>
              <Button
                onClick={() => editSupplierPaymentModalRef.current.show()}
                className="ms-2"
              >
                <FontAwesomeIcon icon={faEdit} />
              </Button>
            </div>
            <div
              style={paymentProofImage ? { width: '30%' } : { width: '25%' }}
            >
              <Card.Title as="h6">
                Payment ID: {supplierPayment?.supplierPaymentId}
              </Card.Title>
              <Card.Title as="h6">
                Payment Method: {supplierPayment?.supplierPaymentMethod}
              </Card.Title>
              {supplierPayment?.checkNumber && (
                <Card.Title as="h6">
                  Check Number: {supplierPayment.checkNumber}
                </Card.Title>
              )}
              <Card.Title as="h6">
                Status: <BadgeStatus status={supplierPayment?.status} />
              </Card.Title>
            </div>
            {paymentProofImage && (
              <div style={{ width: '30%' }}>
                <img
                  onClick={() => imageModalRef.current.show()}
                  src={paymentProofImage}
                  alt="payment proof"
                  style={{
                    margin: 'auto',
                    width: '75%',
                    height: '90%',
                    cursor: 'pointer'
                  }}
                />
              </div>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            <div className="d-flex">
              <div className="w-75">
                <div className="placeholder col-5 w-75 placeholder-glow"></div>
                <div className="placeholder col-5 w-50 placeholder-glow"></div>
                <div className="placeholder d-block col-5 w-25 placeholder-glow"></div>
              </div>
              <div className="w-25">
                <div className="placeholder col-5 w-75 placeholder-glow"></div>
                <div className="placeholder col-5 w-100 placeholder-glow "></div>
                <div className="placeholder col-5 w-75 placeholder-glow"></div>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {supplierPayment?.isCheckNumberPossibleMismatch && (
        <Alert variant="warning" className="mb-4">
          <div className="fw-bold mb-2">Possible check number mismatch</div>
          <div className="mb-3">
            The uploaded check image may not match check number{' '}
            <strong>{supplierPayment.checkNumber}</strong>. Update the number or
            confirm that the entered value is correct.
          </div>
          <div className="d-flex flex-wrap align-items-end gap-2">
            <Form.Group style={{ minWidth: '12rem' }}>
              <Form.Label className="mb-1">Check Number</Form.Label>
              <Form.Control
                type="text"
                value={checkNumberDraft}
                onChange={e => setCheckNumberDraft(e.target.value)}
              />
            </Form.Group>
            <Button
              variant="primary"
              onClick={() => handleUpdateCheckNumber(checkNumberDraft)}
            >
              Update Number
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleConfirmCheckNumber}
            >
              Confirm Entered Number
            </Button>
          </div>
        </Alert>
      )}

      {initiateSelection && (
        <div>
          <div className="mb-6">
            <h3 className="mb-3">Invoices</h3>
            <AdvanceTableProvider {...invoiceTable}>
              <AdvanceTable
                tableProps={{
                  size: 'lg',
                  className:
                    'phoenix-table fs-9 mb-0 p-5 border-top border-translucent'
                }}
                rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
                bodyClassName="py-3"
              />
              <AdvanceTableFooter pagination />
            </AdvanceTableProvider>
          </div>
          {procurementReturns.length !== 0 && (
            <div className="mb-4">
              <h3 className="mb-3">Returns</h3>
              <AdvanceTableProvider {...procurementReturnsTable}>
                <AdvanceTable
                  tableProps={{
                    size: 'lg',
                    className:
                      'phoenix-table fs-9 mb-0 p-5 border-top border-translucent'
                  }}
                  rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
                  bodyClassName="py-3"
                />
                <AdvanceTableFooter pagination />
              </AdvanceTableProvider>
            </div>
          )}
          <div className="d-flex gap-2">
            {(invoice.length !== 0 || returns.length !== 0) && (
              <Button
                variant="danger"
                className="mb-5"
                onClick={() => setInitiateSelection(false)}
              >
                Cancel
              </Button>
            )}
            {(invoices.length !== 0 || procurementReturns.length !== 0) && (
              <Button
                variant="primary"
                className="mb-5"
                onClick={handleSubmitSelection}
                disabled={
                  selectedInvoice.length == 0 && selectedReturns.length == 0
                    ? true
                    : false
                }
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      )}

      {showReturns && (
        <div>
          {invoice.length !== 0 && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0">Payment Invoices</h3>
                {isAdmin && (
                  <Button
                    variant="danger"
                    disabled={selectedPaymentInvoiceIds.length === 0}
                    onClick={() => deleteInvoicesModalRef.current.show()}
                  >
                    Delete selected invoices
                  </Button>
                )}
              </div>
              <AdvanceTableProvider {...paymentInvoiceTable}>
                <AdvanceTable
                  tableProps={{
                    size: 'lg',
                    className:
                      'phoenix-table fs-9 mb-0 p-5 border-top border-translucent'
                  }}
                  rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
                  bodyClassName="py-3"
                />
              </AdvanceTableProvider>
            </div>
          )}
          {returns.length !== 0 && (
            <div className="mb-4">
              <h3 className="mb-3">Payment Returns</h3>
              <AdvanceTableProvider {...paymentReturnTable}>
                <AdvanceTable
                  tableProps={{
                    size: 'lg',
                    className:
                      'phoenix-table fs-9 mb-0 p-5 border-top border-translucent'
                  }}
                  rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
                  bodyClassName="py-3"
                />
              </AdvanceTableProvider>
            </div>
          )}
          <Button
            variant="primary"
            className="w-10 mb-3  me-2"
            onClick={() => modalRef.current.show()}
          >
            Upload Payment Proof
          </Button>
        </div>
      )}

      <CustomModal
        ref={modalRef}
        title="Create Payment Proof"
        content={
          <CreatePaymentProofForm
            ref={formRef}
            supplierId={supplierPaymentId as string}
            branchDomain={selectedBranch?.branchDomain as string}
            userId={userDetails?.userId as number}
          />
        }
        onCancel={() => modalRef.current.hide()}
        onSubmit={handleSubmitProofOfPayment}
      />

      <CustomModal
        ref={releaseCheckModalRef}
        title="Release Check"
        content={
          <ReleaseCheckForm
            ref={releaseCheckFormRef}
            branchDomain={selectedBranch?.branchDomain as string}
            supplierPaymentId={supplierPaymentId as string}
          />
        }
        onCancel={() => releaseCheckModalRef.current.hide()}
        onSubmit={handleReleaseCheck}
      />

      <CustomModal
        ref={confirmCheckModalRef}
        title="Release Check"
        content={
          <ConfirmCheckForm
            ref={confirmCheckFormRef}
            branchDomain={selectedBranch?.branchDomain as string}
            supplierPaymentId={supplierPaymentId as string}
          />
        }
        onCancel={() => confirmCheckModalRef.current.hide()}
        onSubmit={handleConfirmCheck}
      />

      <CustomModal
        ref={editSupplierPaymentModalRef}
        title="Edit Supplier Payment"
        content={
          <EditSupplierPaymentForm
            ref={editSupplierPaymentFormRef}
            defaultValues={{
              supplierPaymentMethod: supplierPayment?.supplierPaymentMethod,
              accountDate: supplierPayment?.accountDate,
              supplierPaymentId: supplierPayment?.supplierPaymentId,
              paymentNotes: supplierPayment?.paymentNotes || '',
              procurementInvoiceIds: [0],
              procurementReturnIds: [0]
            }}
          />
        }
        onCancel={() => editSupplierPaymentModalRef.current.hide()}
        onSubmit={handleSubmitEditedForm}
      />

      <CustomModal
        ref={deletePaymentModalRef}
        title="Delete supplier payment"
        content={
          <p>
            Delete this supplier payment? Attached invoices and returns will be
            removed from the payment, and invoices will be marked unpaid.
          </p>
        }
        onCancel={() => deletePaymentModalRef.current.hide()}
        onSubmit={handleDeletePayment}
        onSubmitCustomDisplay={isDeletingPayment ? 'Deleting…' : 'Delete'}
      />

      <CustomModal
        ref={deleteInvoicesModalRef}
        title="Delete selected invoices"
        content={
          <p>
            Remove {selectedPaymentInvoiceIds.length} invoice
            {selectedPaymentInvoiceIds.length === 1 ? '' : 's'} from this
            payment? They will be marked unpaid so they can be paid again. The
            invoice records will not be deleted.
          </p>
        }
        onCancel={() => deleteInvoicesModalRef.current.hide()}
        onSubmit={handleDeleteSelectedInvoices}
        onSubmitCustomDisplay={
          isDeletingInvoices ? 'Deleting…' : 'Delete selected'
        }
      />

      <ImageModal
        ref={imageModalRef}
        image={paymentProofImage as string}
        size="lg"
        googleFileId={supplierPayment?.paymentProofImageFileId}
        caption="Payment proof"
      />
    </>
  );
};

export default SupplierPaymentDetails;
